import { useState, useRef } from "react";
import SignatureCanvas from "react-signature-canvas";
import { jsPDF } from "jspdf";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Label } from "./ui/label";
import { X, FileText, Pen, Check, RotateCcw } from "lucide-react";

const AGREEMENT_TEXT = `
ADVANTA+ AFFILIATE PARTNER TERMS & CONDITIONS

This Affiliate Agreement ("Agreement") is entered into as of the date of electronic signature below, by and between Advanta Plus ("Company") and the undersigned affiliate partner ("Affiliate").

ENGLISH VERSION

1. INTRODUCTION

These Terms govern participation in the Advanta+ Affiliate Programme.

2. ROLE OF THE AFFILIATE

Promote Advanta+ to customers only and drive sales of membership cards.

3. COMMISSION STRUCTURE

Fixed fee per card sale (4 card types). Amounts agreed at onboarding.

4. PAYMENT TERMS

Payments go directly to the Affiliate's own Stripe account. Affiliate responsible for setup and taxes.

5. TRACKING

Sales tracked via links, QR codes, or identifiers.

6. RESPONSIBILITIES

Promote honestly, follow brand rules, comply with laws.

7. PROHIBITED ACTIVITIES

No spam, false claims, or brand damage.

8. TERRITORY

Assigned Territory as specified at onboarding.

9. INTELLECTUAL PROPERTY

All intellectual property belongs to Advanta+.

10. TERMINATION

14 days notice. Immediate termination for breach.

11. LIABILITY

Advanta+ not liable for affiliate performance.

12. DATA PROTECTION

GDPR compliant.

13. GOVERNING LAW

Spain.

14. ACCEPTANCE

Participation implies acceptance.


VERSIÓN EN ESPAÑOL

1. INTRODUCCIÓN

Estos términos regulan la participación en el programa de afiliados Advanta+.

2. ROL DEL AFILIADO

Promocionar Advanta+ solo a clientes y generar ventas de tarjetas.

3. COMISIONES

Tarifa fija por venta. 4 tipos de tarjetas. Se acuerda al inicio.

4. PAGOS

Pagos directos a la cuenta Stripe del afiliado. Responsable de impuestos.

5. SEGUIMIENTO

Seguimiento mediante enlaces o códigos QR.

6. RESPONSABILIDADES

Promoción honesta y cumplimiento legal.

7. ACTIVIDADES PROHIBIDAS

Prohibido spam o información engañosa.

8. TERRITORIO

Territorio asignado según lo especificado al inicio.

9. PROPIEDAD

Todos los derechos pertenecen a Advanta+.

10. TERMINACIÓN

14 días de aviso.

11. RESPONSABILIDAD

Advanta+ no es responsable del rendimiento.

12. PROTECCIÓN DE DATOS

Cumplimiento RGPD.

13. LEGISLACIÓN

España.

14. ACEPTACIÓN

La participación implica aceptación.

By signing below, the Affiliate acknowledges that they have read, understood, and agree to be bound by the terms and conditions of this Agreement in both English and Spanish versions.
`;

const AffiliateAgreement = ({ affiliateName, territory, onSign, onCancel }) => {
  const [agreed, setAgreed] = useState(false);
  const [signed, setSigned] = useState(false);
  const [generating, setGenerating] = useState(false);
  const sigCanvas = useRef(null);

  const clearSignature = () => {
    sigCanvas.current?.clear();
    setSigned(false);
  };

  const handleSignatureEnd = () => {
    if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
      setSigned(true);
    }
  };

  const generatePDF = async () => {
    if (!sigCanvas.current || sigCanvas.current.isEmpty()) {
      return null;
    }

    setGenerating(true);

    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 20;
      const contentWidth = pageWidth - (margin * 2);

      // Title
      doc.setFontSize(18);
      doc.setFont("helvetica", "bold");
      doc.text("ADVANTA+", pageWidth / 2, 20, { align: "center" });
      doc.setFontSize(14);
      doc.text("AFFILIATE PARTNER AGREEMENT", pageWidth / 2, 28, { align: "center" });

      // Date
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      const currentDate = new Date().toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric"
      });
      doc.text(`Date: ${currentDate}`, margin, 40);

      // Affiliate Info
      doc.text(`Affiliate Name: ${affiliateName}`, margin, 48);
      if (territory) {
        doc.text(`Assigned Territory: ${territory}`, margin, 56);
      }

      // Agreement text
      doc.setFontSize(9);
      const lines = doc.splitTextToSize(AGREEMENT_TEXT.trim(), contentWidth);

      let yPosition = 68;
      const lineHeight = 4.5;
      const pageHeight = doc.internal.pageSize.getHeight();

      for (let i = 0; i < lines.length; i++) {
        if (yPosition > pageHeight - 60) {
          doc.addPage();
          yPosition = 20;
        }
        doc.text(lines[i], margin, yPosition);
        yPosition += lineHeight;
      }

      // Signature section
      if (yPosition > pageHeight - 80) {
        doc.addPage();
        yPosition = 20;
      }

      yPosition += 10;
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.text("SIGNATURE", margin, yPosition);

      yPosition += 8;
      doc.setFont("helvetica", "normal");
      doc.text(`Affiliate Name: ${affiliateName}`, margin, yPosition);

      if (territory) {
        yPosition += 6;
        doc.text(`Territory: ${territory}`, margin, yPosition);
      }

      yPosition += 6;
      doc.text(`Date Signed: ${currentDate}`, margin, yPosition);

      yPosition += 6;
      doc.text(`Time: ${new Date().toLocaleTimeString("en-GB")}`, margin, yPosition);

      // Add signature image
      yPosition += 10;
      const signatureData = sigCanvas.current.toDataURL("image/png");
      doc.addImage(signatureData, "PNG", margin, yPosition, 60, 30);

      // Signature line
      doc.setDrawColor(0);
      doc.line(margin, yPosition + 32, margin + 80, yPosition + 32);
      doc.setFontSize(8);
      doc.text("Authorized Signature", margin, yPosition + 38);

      // Footer
      const docId = `AFF-${Date.now()}-${Math.random().toString(36).substr(2, 9).toUpperCase()}`;
      doc.setFontSize(7);
      doc.setTextColor(128);
      doc.text(`Document ID: ${docId}`, margin, pageHeight - 10);
      doc.text("This document was electronically signed via Advanta Plus Affiliate Portal", margin, pageHeight - 6);

      const pdfBlob = doc.output("blob");
      return pdfBlob;
    } catch (error) {
      console.error("Error generating PDF:", error);
      return null;
    } finally {
      setGenerating(false);
    }
  };

  const handleSubmit = async () => {
    const pdfBlob = await generatePDF();
    if (pdfBlob) {
      onSign(pdfBlob);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-[#0B0F19] rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden border border-[#D4AF37]/20 flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-white/10 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
              <FileText className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <div>
              <h2 className="font-['Playfair_Display'] text-xl text-white">Affiliate Agreement</h2>
              <p className="text-white/50 text-sm">Please review and sign to continue</p>
            </div>
          </div>
          <button
            onClick={onCancel}
            className="text-white/50 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Agreement Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="bg-white/5 rounded-xl p-6 mb-6">
            <pre className="text-white/80 text-sm whitespace-pre-wrap font-sans leading-relaxed">
              {AGREEMENT_TEXT}
            </pre>
          </div>

          {/* Agreement Checkbox */}
          <div className="flex items-start gap-3 mb-6 p-4 bg-[#D4AF37]/5 rounded-xl border border-[#D4AF37]/20">
            <Checkbox
              id="agree-affiliate"
              checked={agreed}
              onCheckedChange={setAgreed}
              className="border-[#D4AF37]/50 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37] mt-1"
            />
            <Label htmlFor="agree-affiliate" className="text-white/80 text-sm leading-relaxed cursor-pointer">
              I, <strong className="text-[#D4AF37]">{affiliateName}</strong>,
              have read and agree to the Affiliate Partner Terms & Conditions in both English and Spanish.
            </Label>
          </div>

          {/* Signature Section */}
          {agreed && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-white">
                <Pen className="w-5 h-5 text-[#D4AF37]" />
                <span className="font-medium">Your Signature</span>
              </div>

              <div className="relative bg-white rounded-xl overflow-hidden">
                <SignatureCanvas
                  ref={sigCanvas}
                  canvasProps={{
                    className: "w-full h-40",
                    style: { width: "100%", height: "160px" }
                  }}
                  backgroundColor="white"
                  penColor="black"
                  onEnd={handleSignatureEnd}
                />
                {!signed && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <span className="text-gray-400 text-sm">Draw your signature here</span>
                  </div>
                )}
              </div>

              <button
                type="button"
                onClick={clearSignature}
                className="flex items-center gap-2 text-white/50 hover:text-white text-sm transition-colors"
              >
                <RotateCcw className="w-4 h-4" />
                Clear signature
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-white/10 flex justify-end gap-4 flex-shrink-0">
          <Button
            variant="ghost"
            onClick={onCancel}
            className="text-white/70 hover:text-white"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!agreed || !signed || generating}
            className="btn-gold px-8"
          >
            {generating ? (
              "Generating..."
            ) : (
              <>
                <Check className="w-4 h-4 mr-2" />
                Sign & Submit
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AffiliateAgreement;
